// Refer-a-friend — storefront pieces.
//
//   ReferralsPage      /account/referrals (also /referrals): invite link, share
//                      buttons, terms, invite list. Ineligible users see the
//                      unlock progress instead of a link.
//   ReferralPromoCard  post-order success card ("Give $1, get $1")
//   InviteBanner       home-page notice for visitors who arrived via an invite
//
// Data: GET /api/referrals (auth) and GET /api/referrals/terms (public).
// Rules: lib/referrals.js, docs/todo/referral-program.md.
import React, { useEffect, useState } from 'react';
import { track } from './Chrome';
import { AccountShell, useRequireAuth } from './Account';

const MT = () => (typeof window !== 'undefined' && window.__MT) || {};

function fmtUsd(n) { return `$${Number(n || 0).toFixed(2)}`; }

function useReferrals(me) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!me) return;
    fetch('/api/referrals', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData)
      .catch((e) => setError(e.message));
  }, [me]);
  return { data, error };
}

export function useReferralTerms() {
  const [terms, setTerms] = useState(null);
  useEffect(() => {
    fetch('/api/referrals/terms').then((r) => (r.ok ? r.json() : null)).then((d) => d && setTerms(d.terms)).catch(() => {});
  }, []);
  return terms;
}

function shareLinks(url, text) {
  const enc = encodeURIComponent;
  const links = [
    ['WhatsApp', `https://wa.me/?text=${enc(`${text} ${url}`)}`],
    ['LINE', `https://social-plugins.line.me/lineit/share?url=${enc(url)}`],
    ['Telegram', `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`],
    ['Facebook', `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`],
  ];
  const appId = MT().facebookAppId;
  if (appId && appId !== 'undefined') {
    links.splice(3, 0, ['Messenger', `https://www.facebook.com/dialog/send?app_id=${enc(appId)}&link=${enc(url)}&redirect_uri=${enc(url)}`]);
  }
  return links;
}

export function ShareRow({ url, text, source }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    track('referral_share', { channel: 'copy', source });
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }
  return (
    <div className="ref-share">
      <div className="ref-linkbox">
        <input className="coinput mono-sm" readOnly value={url} onFocus={(e) => e.target.select()} aria-label="Your invite link" />
        <button type="button" className="btn small" onClick={copy}>{copied ? 'Copied!' : 'Copy'}</button>
      </div>
      <div className="ref-share-btns">
        {typeof navigator !== 'undefined' && navigator.share && (
          <button type="button" className="btn small ghosted" onClick={() => {
            track('referral_share', { channel: 'native', source });
            navigator.share({ title: 'MobileTopup.com', text, url }).catch(() => {});
          }}>Share…</button>
        )}
        {shareLinks(url, text).map(([name, href]) => (
          <a key={name} className="btn small ghosted" href={href} target="_blank" rel="noopener noreferrer"
            onClick={() => track('referral_share', { channel: name.toLowerCase(), source })}>{name}</a>
        ))}
      </div>
    </div>
  );
}

function TermsText({ terms }) {
  if (!terms) return null;
  return (
    <ul className="ref-bullets cohint" style={{ marginTop: 12 }}>
      <li>You and your friend each get {fmtUsd(terms.rewardUsd)} of wallet credit</li>
      <li>Their first order must be ฿{terms.floorThb} / ${terms.floorUsd} or more, delivered</li>
      <li>Credit is paid after a {terms.holdDays}-day hold</li>
      <li>Friends must order within {terms.windowDays} days of joining</li>
      <li>Up to {terms.monthlyCap} rewards a month</li>
      <li>Credit is USD wallet balance for MobileTopup purchases only — no withdrawals or transfers</li>
      <li>Refunded or disputed orders don't count</li>
      <li><a href="/terms#referrals">Full terms</a></li>
    </ul>
  );
}

function UnlockCard({ data }) {
  const e = data.eligibility;
  if (e.partner) {
    return (
      <div className="cocard">
        <h3 className="coh">Refer a friend</h3>
        <ul className="ref-bullets">
          <li>Refer-a-friend is for retail customers</li>
          <li>As a partner you already earn on every order through your <a href={data.terms.partnerUrl}>partner account</a></li>
        </ul>
      </div>
    );
  }
  const left = Math.max(0, e.minPaidOrders - e.paidOrders);
  return (
    <div className="cocard">
      <h3 className="coh">Unlock your invite link</h3>
      <ul className="ref-bullets">
        <li>Your invite link opens once you've used MobileTopup yourself</li>
        <li>Then every friend you bring earns you both {fmtUsd(data.terms.rewardUsd)}</li>
      </ul>
      <ul className="ref-unlock">
        <li className={e.verifiedEmail ? 'done' : ''}>
          <span className="ref-tick" aria-hidden="true">{e.verifiedEmail ? '✓' : '○'}</span>
          <span>Verified email {e.verifiedEmail ? '' : <>— <a href="/settings">check your inbox or resend</a></>}</span>
        </li>
        <li className={left === 0 ? 'done' : ''}>
          <span className="ref-tick" aria-hidden="true">{left === 0 ? '✓' : '○'}</span>
          <span>{e.paidOrders} of {e.minPaidOrders} paid orders{left > 0 && <> — <a href="/">{left} more to go</a></>}</span>
        </li>
      </ul>
      <TermsText terms={data.terms} />
    </div>
  );
}

function InviteRow({ inv }) {
  const s = inv.status || {};
  const cls = { rewarded: 'good', pending: 'dim', signed_up: 'dim', capped: 'bad', reversed: 'bad', expired: 'dim', not_eligible: 'bad' }[s.key] || 'dim';
  return (
    <div className="acct-row">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div>{inv.friend}</div>
        <div className="cohint">{s.label}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <span className={`tag ${cls}`}>{inv.rewardUsd ? `+${fmtUsd(inv.rewardUsd)}` : (s.key === 'pending' ? 'on hold' : s.key.replace('_', ' '))}</span>
        <div className="cohint">{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : ''}</div>
      </div>
    </div>
  );
}

export function ReferralsPage({ me, currency, onCurrency }) {
  useRequireAuth(me, '/account/referrals');
  const { data, error } = useReferrals(me);
  if (me === undefined) return null;

  let body;
  if (error) body = <div className="cocard"><div className="notice bad">Couldn't load your referrals: {error}</div></div>;
  else if (!data) body = <div className="cocard"><div className="acct-row skeleton" /><div className="acct-row skeleton" /></div>;
  else if (!data.enabled) body = <div className="cocard"><h3 className="coh">Refer a friend</h3><p>The referral program is paused right now. Check back soon.</p></div>;
  else if (!data.eligible) body = <UnlockCard data={data} />;
  else {
    const shareText = `Get $${data.terms.rewardUsd} of credit on your first top-up at MobileTopup.com — use my invite link:`;
    body = (
      <>
        <div className="cocard">
          <h3 className="coh">Give {fmtUsd(data.terms.rewardUsd)}, get {fmtUsd(data.terms.rewardUsd)}</h3>
          <ul className="ref-bullets">
            <li>Share your link</li>
            <li>Your friend signs up through it</li>
            <li>Their first order of ฿{data.terms.floorThb} / ${data.terms.floorUsd}+ is delivered</li>
            <li>You both get {fmtUsd(data.terms.rewardUsd)} of wallet credit</li>
          </ul>
          <ShareRow url={data.link} text={shareText} source="referrals_page" />
          {data.stats.capReached && (
            <div className="notice" style={{ marginTop: 12 }}>
              <ul className="ref-bullets">
                <li>You've hit this month's limit of {data.terms.monthlyCap} rewards</li>
                <li>Bringing in that many customers? <a href={data.terms.partnerUrl}>Apply to the Partner program</a> for wholesale rates</li>
              </ul>
            </div>
          )}
          <TermsText terms={data.terms} />
        </div>

        <div className="cocard">
          <h3 className="coh">Your invites</h3>
          <div className="ref-stats">
            <div><b>{data.stats.invited}</b><span>invited</span></div>
            <div><b>{data.stats.pending}</b><span>on hold</span></div>
            <div><b>{data.stats.rewarded}</b><span>rewarded</span></div>
            <div><b>{fmtUsd(data.stats.earnedUsd)}</b><span>earned</span></div>
          </div>
          {data.invites.length === 0
            ? <ul className="ref-bullets cohint"><li>No invites yet</li><li>Send your link to someone who tops up abroad — seafarers, travellers, family overseas</li></ul>
            : <div className="acct-list">{data.invites.map((inv) => <InviteRow key={inv.id} inv={inv} />)}</div>}
        </div>
      </>
    );
  }

  return (
    <AccountShell me={me} currency={currency} onCurrency={onCurrency} active="referrals" title="Refer a friend"
      sub="Share MobileTopup, earn wallet credit together.">
      {body}
    </AccountShell>
  );
}

// Post-order success card. Only renders for eligible inviters (one small
// request); guests and not-yet-eligible customers see nothing.
export function ReferralPromoCard({ me, source }) {
  const { data } = useReferrals(me);
  if (!me || !data || !data.enabled || !data.eligible) return null;
  const amt = fmtUsd(data.terms.rewardUsd);
  return (
    <div className="cocard ref-promo">
      <h3 className="coh">Give {amt}, get {amt}</h3>
      <ul className="ref-bullets cohint" style={{ marginBottom: 8 }}>
        <li>Know someone who tops up abroad? Share your invite link</li>
        <li>You both get {amt} of wallet credit after their first order</li>
      </ul>
      <ShareRow url={data.link} text={`Get $${data.terms.rewardUsd} of credit on your first top-up at MobileTopup.com — use my invite link:`} source={source || 'success_page'} />
      <p className="cohint" style={{ marginTop: 8 }}><a href="/account/referrals">See your invites</a></p>
    </div>
  );
}

// Shown on the home page after /invite/<code> (?ref=1 or the attribution
// cookie exposed as __MT.refCode). Dismissal is per browser.
const BANNER_KEY = 'mt_ref_banner_dismissed';
export function InviteBanner({ me }) {
  const [visible, setVisible] = useState(false);
  const terms = useReferralTerms();
  useEffect(() => {
    if (me) return;
    let arrived = false;
    try {
      arrived = new URLSearchParams(window.location.search).get('ref') === '1' || !!MT().refCode;
      if (arrived && localStorage.getItem(BANNER_KEY)) arrived = false;
    } catch (e) { /* ignore */ }
    setVisible(arrived);
  }, [me]);
  if (!visible) return null;
  const amt = terms ? fmtUsd(terms.rewardUsd) : '$1.00';
  const floor = terms ? `฿${terms.floorThb} / $${terms.floorUsd}` : '฿300 / $10';
  return (
    <div className="ref-banner" role="status">
      <span aria-hidden="true">🎁</span>
      <div>
        <b>Your friend gave you {amt} of credit.</b> Create an account, and after your first order of {floor} or more is delivered
        the credit lands in your wallet.{' '}
        <a href="/signup">Sign up</a>
      </div>
      <button type="button" className="icon-btn" aria-label="Dismiss" onClick={() => { try { localStorage.setItem(BANNER_KEY, '1'); } catch (e) { /* ignore */ } setVisible(false); }}>×</button>
    </div>
  );
}
